import axios from "axios";

const invoiceUrl = "/v2/api/invoices";

class InvoiceService {
    // Search for all invoices with optional query parameters
    static searchAllInvoices(urlParams) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}${urlParams}`;
                const result = await axios.get(url);
                const pagingData = result.data;
                resolve(pagingData);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Get a specific invoice by its ID
    static getInvoiceById(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}`;
                const result = await axios.get(url);
                const invoiceData = result.data;
                resolve(invoiceData);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Create a new invoice
    static createInvoice(invoiceData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = invoiceUrl;
                const result = await axios.post(url, invoiceData);
                const createdInvoice = result.data;
                resolve(createdInvoice);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Update an existing invoice
    static updateInvoice(invoiceId, updatedInvoiceData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}`;
                await axios.put(url, updatedInvoiceData);
                resolve(`Invoice with ID ${invoiceId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Cancel an invoice: it stays on record, marked Cancelled
    static cancelInvoice(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}/cancel`;
                const result = await axios.post(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Delete an invoice by its ID
    static deleteInvoice(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}`;
                await axios.delete(url);
                resolve(`Invoice with ID ${invoiceId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Create an invoice item for a specific invoice
    static createInvoiceItem(invoiceId, invoiceItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}/items`;
                const result = await axios.post(url, invoiceItemData);
                const createdInvoiceItem = result.data;
                resolve(createdInvoiceItem);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Update an existing invoice item
    static updateInvoiceItem(invoiceId, invoiceItemId, updatedInvoiceItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}/items/${invoiceItemId}`;
                await axios.put(url, updatedInvoiceItemData);
                resolve(`Invoice item with ID ${invoiceItemId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Delete an invoice item from a specific invoice
    static deleteInvoiceItem(invoiceId, invoiceItemId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}/items/${invoiceItemId}`;
                await axios.delete(url);
                resolve(`Invoice item with ID ${invoiceItemId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Generate an invoice from a sales order
    static convertFromSalesOrder(salesOrderId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/convert-from-sales-order/${salesOrderId}`;
                const result = await axios.post(url);
                const convertedInvoice = result.data;
                resolve(convertedInvoice);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Turn a pro-forma into a real invoice: it is issued a new invoice number
    static convertProFormaToInvoice(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${invoiceUrl}/${invoiceId}/convert-to-invoice`;
                const result = await axios.post(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Generate an invoice report
    static async generateInvoiceReport(invoiceId, reportType) {
        try {
            const url = `${invoiceUrl}/${invoiceId}/report/${reportType}`;
            const response = await axios.post(url, {}, { responseType: 'blob' });
            return response.data;
        } catch (error) {
            throw new Error(error.response ? error.response.data : error.message);
        }
    }
}

export default InvoiceService;
